import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Input, Popover, message } from '@myua/kit';
import { getUser } from '../../app/selectors';
import { getAssetLabLogger } from '../../app/logs';
import {
  aiAssetName,
  aiMetaFor,
  assetHasCategory,
  firstRefBaseName,
  MagicPrimaryAsset,
} from '../model';
import { mimeFromDataUrl } from '../imageFormat';
import { Destination, SavedAssetLink } from '../saveToAssets/types';
import { getRecentDestinations, pushRecentDestination } from '../saveToAssets/recentDestinations';
import { uploadGenerationToAssets } from '../saveToAssets/uploadGeneration';
import { CategoryPickerModal } from './CategoryPickerModal';
import { MagicBtn } from './ui';
import { TM } from '../tokens';
import { CheckIcon, PlusIcon, SaveToAssetsIcon } from '../icons';

// The reference shape both the live studio (MagicRef) and a history card
// (HistoryRefView, mapped at the call site) can pass.
export type SaveRefLike =
  | { kind: 'asset'; asset: MagicPrimaryAsset }
  | { kind: 'upload'; name: string };

// Build a save destination from a reference's source asset — only possible when
// the ref was staged from the detail-panel sidebar (a full AssetDetails with a
// non-null category + project). Null otherwise.
function deriveDestination(ref: SaveRefLike | null): Destination | null {
  if (!ref || ref.kind !== 'asset' || !assetHasCategory(ref.asset)) return null;
  const { project, category } = ref.asset;
  return {
    projectId: project.id,
    projectName: project.name,
    categoryId: category.id,
    categoryName: category.name,
    categoryPath: category.path,
  };
}

const DestinationRow: React.FC<{
  dest: Destination;
  badge?: string;
  selected: boolean;
  onClick: () => void;
}> = ({ dest, badge, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      textAlign: 'left',
      padding: '6px 8px',
      borderRadius: 6,
      border: `1px solid ${selected ? TM.blue : 'transparent'}`,
      background: selected ? TM.blueFaint : 'transparent',
      cursor: 'pointer',
    }}
  >
    <span
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: `1.5px solid ${selected ? TM.blue : TM.borderStrong}`,
        background: selected ? TM.blue : '#fff',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {selected && <CheckIcon size={9} />}
    </span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: 'block',
          fontSize: 12,
          color: TM.text,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {dest.categoryName}
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 10,
          color: TM.textFaint,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {dest.projectName}
      </span>
    </span>
    {badge && (
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          padding: '1px 5px',
          borderRadius: 3,
          background: TM.blueFaint,
          color: TM.blue,
          flexShrink: 0,
        }}
      >
        {badge}
      </span>
    )}
  </button>
);

export const SaveToAssetsButton: React.FC<{
  imageUrl: string | null;
  firstRef: SaveRefLike | null;
  model: string;
  // The on-device tool that produced the image, if any — drives the embedded
  // Digital Source Type (a tool output is an AI edit, not a fresh generation).
  source?: 'removeBg' | 'upscale';
  // Generation timestamp for the filename date (active result → now).
  createdAt?: number;
  // The image's own mime type, when the caller already holds the bytes (a
  // history card has the Blob; a live result carries it in its data URL). Drives
  // the prefilled filename's extension — see aiAssetName. The upload itself
  // re-checks the bytes, so a wrong guess here can't mislabel the saved file.
  mimeType?: string | null;
  // Trigger style: a labelled button (footer) or a compact icon (history card).
  variant?: 'button' | 'icon';
  disabled?: boolean;
  // Called after a successful save with the created asset link, so the host can
  // mark the matching history record as saved.
  onSaved?: (link: SavedAssetLink) => void;
}> = ({
  imageUrl,
  firstRef,
  model,
  source,
  createdAt,
  mimeType,
  variant = 'button',
  disabled,
  onSaved,
}) => {
  const user = useSelector(getUser);
  const userId = user ? String(user.id) : '';
  const assetLabLogger = useSelector(getAssetLabLogger);

  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [recents, setRecents] = useState<Destination[]>([]);
  const [dest, setDest] = useState<Destination | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const derived = useMemo(() => deriveDestination(firstRef), [firstRef]);
  const defaultName = useMemo(
    () =>
      aiAssetName(
        firstRefBaseName(firstRef),
        model,
        createdAt ? new Date(createdAt) : new Date(),
        mimeType ?? mimeFromDataUrl(imageUrl),
      ),
    [firstRef, model, createdAt, mimeType, imageUrl],
  );

  const inactive = disabled || !imageUrl;

  // Destinations shown in the popover: the derived one first (badged), then the
  // recent ones (excluding a duplicate of the derived).
  const options = useMemo(() => {
    const list: { dest: Destination; badge?: string }[] = [];
    if (derived) list.push({ dest: derived, badge: 'From reference' });
    recents
      .filter((r) => r.categoryId !== derived?.categoryId)
      .forEach((r) => list.push({ dest: r }));
    return list;
  }, [derived, recents]);

  const prime = () => {
    const r = getRecentDestinations(userId);
    setRecents(r);
    setName(defaultName);
    const initial = derived ?? r[0] ?? null;
    setDest(initial);
    return initial;
  };

  const openPicker = () => {
    setOpen(false);
    setPickerOpen(true);
  };

  const doSave = async () => {
    if (!imageUrl || !dest || !name.trim() || saving) return;
    setSaving(true);
    try {
      const assetId = await uploadGenerationToAssets({
        imageUrl,
        name: name.trim(),
        dest,
        meta: aiMetaFor(model, source),
      });
      pushRecentDestination(userId, dest);
      assetLabLogger({
        action: 'Magic Studio',
        sub_action: 'Save to Assets',
        extra: { int_1: assetId, text_1: model, text_2: dest.categoryId },
      });
      void message.success(`Saved to ${dest.projectName} / ${dest.categoryName}`);
      onSaved?.({ ...dest, assetId, savedAt: Date.now() });
      setOpen(false);
    } catch (e) {
      void message.error(e instanceof Error ? e.message : 'Could not save to Asset Lab');
    } finally {
      setSaving(false);
    }
  };

  const trigger =
    variant === 'icon' ? (
      <button
        type="button"
        title="Save to assets"
        disabled={inactive}
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          cursor: inactive ? 'default' : 'pointer',
          border: 'none',
          background: 'rgba(255,255,255,0.92)',
          color: TM.text,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
          opacity: inactive ? 0.5 : 1,
        }}
      >
        <SaveToAssetsIcon size={14} />
      </button>
    ) : (
      <MagicBtn kind="secondary" disabled={inactive} icon={<SaveToAssetsIcon size={13} />}>
        Save to assets
      </MagicBtn>
    );

  // Disabled → render the trigger alone (no popover / picker).
  if (inactive) return trigger;

  const content = (
    <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <button
        type="button"
        onClick={openPicker}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 2px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: TM.blue,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <PlusIcon size={12} />
        Pick another category
      </button>

      {options.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: 184,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: TM.textFaint,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              padding: '0 2px 2px',
            }}
          >
            Recent destinations
          </div>
          {options.map(({ dest: d, badge }) => (
            <DestinationRow
              key={d.categoryId}
              dest={d}
              badge={badge}
              selected={dest?.categoryId === d.categoryId}
              onClick={() => setDest(d)}
            />
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: TM.textFaint }}>
          No destination yet — pick a category above.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, color: TM.textFaint }}>Asset name</span>
        <Input
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={() => void doSave()}
        />
        <span style={{ fontSize: 10, color: TM.textFaint }}>
          Tagged <b style={{ color: TM.blue }}>AI Generated</b>
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <MagicBtn kind="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </MagicBtn>
        <MagicBtn
          kind="magic"
          size="sm"
          disabled={!dest || !name.trim() || saving}
          onClick={() => void doSave()}
        >
          {saving ? 'Saving…' : 'Save'}
        </MagicBtn>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        trigger="click"
        open={open}
        placement={variant === 'icon' ? 'bottomRight' : 'topRight'}
        content={content}
        onOpenChange={(next) => {
          if (next) {
            const initial = prime();
            if (initial) setOpen(true);
            else openPicker();
          } else if (!pickerOpen) {
            setOpen(false);
          }
        }}
      >
        {/* Span wrapper gives antd a stable, ref-able DOM node to anchor to
            (MagicBtn doesn't forward a ref). */}
        <span style={{ display: 'inline-flex' }}>{trigger}</span>
      </Popover>
      <CategoryPickerModal
        open={pickerOpen}
        onPick={(d) => {
          setDest(d);
          setPickerOpen(false);
          setOpen(true);
        }}
        onClose={() => {
          setPickerOpen(false);
          setOpen(true);
        }}
      />
    </>
  );
};
